"use client";

import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";


import { addMinutes, format, parseISO, subMinutes } from 'date-fns'
import enUS from 'date-fns/locale/en-US'
import nl from 'date-fns/locale/nl'

import { cn } from '../../lib/utils'
import { useTranslation } from 'react-i18next'

export function DatePickerDemo({
  value,
  setValue,
  minDate,
  maxDate,
}: {
  setValue: (value: string) => void
  value?: string
  minDate?: Date
  maxDate?: Date
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const currentDate = value ? parseISO(value) : undefined
  // console.log({ open, currentDate })
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={'outline'}
          className={cn(
            'cursor-pointer flex justify-between bg-white border border-border text-slate-50 hover:bg-gray-800',
            'w-full pl-3 text-left font-normal',
            !value && 'text-muted-foreground',
          )}
        >
          {currentDate ? (
            format(currentDate, 'dd MMMM yyyy', {
              locale: i18n.language === 'nl' ? nl : enUS,
            })
          ) : (
            <span>{t('pick_a_date')}</span>
          )}
          <CalendarIcon className="ml-auto h-10 w-10 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          mode="single"
          weekStartsOn={1}
          selected={currentDate ?? new Date()}
          defaultMonth={currentDate || new Date()}
          onSelect={(newDate) => {
            if (!newDate) return

            const minutesOffsetToUtc = newDate.getTimezoneOffset()
            const newDateStr =
              minutesOffsetToUtc > 0
                ? addMinutes(newDate, minutesOffsetToUtc)
                : subMinutes(newDate, minutesOffsetToUtc)

            setValue(newDateStr.toISOString())
            setOpen(false)
          }}
          disabled={(date) => {
            if (minDate && date < minDate) return true
            if (maxDate && date > maxDate) return true
            return false
          }}
          locale={i18n.language === 'nl' ? nl : enUS}
        />
      </PopoverContent>
    </Popover>
  )
}
